/**
 * Native API Test panel — 에뮬레이터/디바이스 에서 native wrapper 동작 검증.
 *
 * Phase 2 검증 게이트. 모든 버튼이 native 환경에서는 실제 기능 호출,
 * web 환경에서는 no-op + console.warn (안전).
 */
import { useState } from "react";
import { isNative, getPlatform } from "../native";
import { triggerHaptic, triggerNotification, triggerCelebration } from "../native/haptics";
import { setStatusBar } from "../native/statusBar";
import { useNetworkStatus } from "../native/network";
import { share, canShare } from "../native/share";
import { hideKeyboard } from "../native/keyboard";
import { exitApp } from "../native/app";
import styles from "./NativeTest.module.css";

export default function NativeTest() {
  const network = useNetworkStatus();
  const [log, setLog] = useState<string[]>([]);

  const append = (line: string) => {
    const ts = new Date().toLocaleTimeString();
    setLog((prev) => [`[${ts}] ${line}`, ...prev.slice(0, 19)]);
  };

  const wrap = (name: string, fn: () => Promise<unknown> | void) => async () => {
    try {
      const result = await fn();
      append(`✓ ${name}` + (result !== undefined ? ` → ${JSON.stringify(result)}` : ""));
    } catch (err) {
      append(`✗ ${name} — ${(err as Error).message ?? String(err)}`);
    }
  };

  return (
    <div className={styles.panel}>
      <h3 className={styles.heading}>Native API Test</h3>

      <div className={styles.envRow}>
        <span>platform: <code>{getPlatform()}</code></span>
        <span>native: <code>{String(isNative())}</code></span>
        <span>network: <code>{network.connected ? `${network.type}` : "offline"}</code></span>
      </div>

      <div className={styles.group}>
        <div className={styles.groupTitle}>Haptics</div>
        <button className={styles.btn} onClick={wrap("haptic light", () => triggerHaptic("light"))}>light</button>
        <button className={styles.btn} onClick={wrap("haptic medium", () => triggerHaptic("medium"))}>medium</button>
        <button className={styles.btn} onClick={wrap("haptic heavy", () => triggerHaptic("heavy"))}>heavy</button>
        <button className={styles.btn} onClick={wrap("notif success", () => triggerNotification("success"))}>notif:success</button>
        <button className={styles.btn} onClick={wrap("notif warning", () => triggerNotification("warning"))}>notif:warning</button>
        <button className={styles.btn} onClick={wrap("notif error", () => triggerNotification("error"))}>notif:error</button>
        <button className={styles.btn} onClick={wrap("celebration", () => triggerCelebration())}>celebration (A상)</button>
      </div>

      <div className={styles.group}>
        <div className={styles.groupTitle}>StatusBar</div>
        <button className={styles.btn} onClick={wrap("statusBar yellow+dark", () => setStatusBar({ style: "dark", backgroundColor: "#FBD54E" }))}>yellow + dark</button>
        <button className={styles.btn} onClick={wrap("statusBar black+light", () => setStatusBar({ style: "light", backgroundColor: "#0A0A0A" }))}>black + light</button>
        <button className={styles.btn} onClick={wrap("statusBar white+dark", () => setStatusBar({ style: "dark", backgroundColor: "#FFFFFF" }))}>white + dark</button>
      </div>

      <div className={styles.group}>
        <div className={styles.groupTitle}>Share</div>
        <button
          className={styles.btn}
          onClick={wrap("canShare", async () => await canShare())}
        >
          canShare?
        </button>
        <button
          className={styles.btn}
          onClick={wrap("share", () =>
            share({
              title: "Peeka 알림",
              text: "쿠지 결과를 공유합니다!",
              url: "https://peeka.app/gift/RECEIVE?token=DEMO",
              dialogTitle: "공유하기",
            }),
          )}
        >
          share dialog
        </button>
      </div>

      <div className={styles.group}>
        <div className={styles.groupTitle}>Keyboard</div>
        <input
          className={styles.input}
          placeholder="여기 탭하면 키보드 올라옴"
          onBlur={() => append("input blurred")}
        />
        <button className={styles.btn} onClick={wrap("hideKeyboard", () => hideKeyboard())}>hide keyboard</button>
      </div>

      <div className={styles.group}>
        <div className={styles.groupTitle}>App</div>
        <button className={styles.btn} onClick={wrap("exit app", () => exitApp())}>exit app</button>
      </div>

      <div className={styles.logTitle}>로그 (최근 20개)</div>
      <pre className={styles.log}>{log.length === 0 ? "(아직 호출 없음)" : log.join("\n")}</pre>
    </div>
  );
}
